<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { ApiRequestError, createAdminIntake, getAdminDefinitions } from '@/api/client'
import type { AdminDefinition, AdminIntake } from '@/api/schemas'
import {
  endOfLocalDay,
  factLabel,
  formatLocalTime,
  localDateInput,
  maxExpiryDate,
  newIdempotencyKey,
} from './intakeHelpers'
import { CreateIntakeSchema, firstIssue } from '@/validation'
import { tidy } from '@/lib/inputFormat'
import { useToasts } from '@/lib/toasts'

const definitions = ref<AdminDefinition[]>([])
const clientName = ref('')
const formType = ref('lsa')
const start = new Date()
start.setDate(start.getDate() + 90)
const expiryDate = ref(localDateInput(start))
const minDate = localDateInput(new Date())
const maxDate = maxExpiryDate()
const busy = ref(false)
const loading = ref(true)
const error = ref('')
const created = ref<AdminIntake | null>(null)
// Only the clipboard fallback stays on the page: it points at the field above the buttons.
const copyHelp = ref('')
const toasts = useToasts()
let idempotencyKey = newIdempotencyKey()
let idempotencyPayload: string | undefined
const selected = computed(() =>
  definitions.value.find((definition) => definition.id === formType.value),
)
function tidyClientName() {
  clientName.value = tidy('text', clientName.value) as string
}

onMounted(async () => {
  try {
    definitions.value = (await getAdminDefinitions()).definitions
    if (!definitions.value.some((d) => d.id === formType.value))
      formType.value = definitions.value[0]?.id ?? ''
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError ? cause.message : 'Unable to load intake templates.'
  } finally {
    loading.value = false
  }
})
async function submit() {
  error.value = ''
  const issue = firstIssue(
    CreateIntakeSchema.safeParse({ client_name: clientName.value, form_type: formType.value }),
  )
  if (issue !== undefined) {
    error.value = issue
    return
  }
  const name = clientName.value.trim()
  if (!selected.value) {
    error.value = 'Choose an intake template.'
    return
  }
  let expiresAt: string
  try {
    expiresAt = endOfLocalDay(expiryDate.value)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Choose a valid expiry date.'
    return
  }
  const expiryInstant = new Date(expiresAt).getTime()
  if (expiryInstant <= Date.now()) {
    error.value = 'Choose an expiry date in the future.'
    return
  }
  if (expiryInstant > Date.now() + 365 * 24 * 60 * 60 * 1000) {
    error.value = 'Choose a date within 365 days.'
    return
  }
  const request = { client_name: name, form_type: formType.value, expires_at: expiresAt }
  const payload = JSON.stringify(request)
  if (idempotencyPayload !== undefined && idempotencyPayload !== payload)
    idempotencyKey = newIdempotencyKey()
  idempotencyPayload = payload
  busy.value = true
  try {
    created.value = await createAdminIntake(request, idempotencyKey)
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError
        ? cause.message
        : 'Unable to create intake link. Please retry.'
  } finally {
    busy.value = false
  }
}
async function copy() {
  if (!created.value) return
  try {
    await navigator.clipboard.writeText(created.value.url)
    copyHelp.value = ''
    toasts.success('Link copied.')
  } catch {
    copyHelp.value = 'Select and copy the link below.'
  }
}
</script>

<template>
  <main class="admin-page">
    <header class="admin-header">
      <p class="eyebrow">Administration</p>
      <h1 class="display">Create intake link</h1>
      <p>Create a private intake link for one client.</p>
    </header>
    <section v-if="created" class="admin-card success-card" aria-labelledby="created-title">
      <h2 id="created-title">Intake link created</h2>
      <p>Expires {{ formatLocalTime(created.expires_at) }}.</p>
      <label
        >Client link
        <input
          class="copy-field"
          readonly
          :value="created.url"
          @focus="($event.target as HTMLInputElement).select()"
      /></label>
      <p v-if="copyHelp" class="form-notice" role="status">{{ copyHelp }}</p>
      <div class="dialog-actions">
        <button class="btn btn-primary" @click="copy">Copy link</button
        ><RouterLink class="btn btn-secondary" to="/admin/intakes">Back to intakes</RouterLink>
      </div>
    </section>
    <section v-else class="admin-card">
      <p v-if="loading" aria-busy="true">Loading intake templates…</p>
      <form v-else novalidate @submit.prevent="submit">
        <label
          >Client name
          <input
            v-model="clientName"
            maxlength="200"
            autocapitalize="words"
            required
            :disabled="busy"
            @blur="tidyClientName" /></label
        ><label
          >Form template
          <select v-model="formType" required :disabled="busy">
            <option v-for="definition in definitions" :key="definition.id" :value="definition.id">
              {{ definition.title }}
            </option>
          </select></label
        ><label
          >Expiry date
          <input
            v-model="expiryDate"
            type="date"
            required
            :min="minDate"
            :max="maxDate"
            :disabled="busy"
          /><small>The link expires at the end of this day in your local timezone.</small></label
        >
        <aside v-if="selected" class="template-summary">
          <h2>{{ selected.title }}</h2>
          <p>{{ selected.steps }} steps</p>
          <dl v-if="Object.keys(selected.arranged_facts).length">
            <template v-for="(value, key) in selected.arranged_facts" :key="key"
              ><dt>{{ factLabel(String(key)) }}</dt>
              <dd>{{ Array.isArray(value) ? value.join(', ') : value }}</dd></template
            >
          </dl>
        </aside>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="btn btn-primary" :disabled="busy || !definitions.length">
          {{ busy ? 'Creating link…' : 'Create link' }}
        </button>
      </form>
    </section>
  </main>
</template>
